import React, { useCallback, useEffect, useMemo, useState, Suspense } from "react"
import { toast } from "sonner"
import { CogoApp, type ToastParams } from "@cogo/react-renderer/components/cogo-app"
import { loadApp, loadFromSchemaUrl, type LoadedApp } from "./app-loader"
import { useAuth } from "./auth/auth-context"

declare global {
  interface Window {
    __COGO_APP__?: { slug: string; schemaUrl: string }
  }
}

const VerifyPage = React.lazy(() => import("./pages/VerifyPage"))
const RnPreviewPage = React.lazy(() => import("./pages/RnPreviewPage"))
const ComparePage = React.lazy(() => import("./pages/ComparePage"))
const HomePage = React.lazy(() => import("./pages/homepage/HomePage"))
const AuthPage = React.lazy(() => import("./auth/auth-page").then((m) => ({ default: m.AuthPage })))
const AuthCallback = React.lazy(() => import("./auth/auth-callback").then((m) => ({ default: m.AuthCallback })))
const OnboardingPage = React.lazy(() => import("./auth/onboarding-page").then((m) => ({ default: m.OnboardingPage })))

/**
 * Route prefix → template directory mapping.
 * 28 blocks-v2 quality templates — single canonical route per template.
 * All blocks are symlinks to packages/cogo-schema/blocks-v2/.
 */
const TEMPLATE_MAP: Record<string, string> = {
  // Reference & showcase
  "/golden": "golden-demo",
  "/showcase": "showcase-forms",
  "/todo": "todo-demo",
  "/survey": "survey-demo",
  // E-Commerce
  "/store": "ecommerce-v2",
  // Portfolios
  "/portfolio": "portfolio-v3",
  "/portfolio-v2": "portfolio-site",
  // Blogs
  "/blog": "blog-site",
  "/blog-v2": "blog-platform",
  // Dashboards & Analytics
  "/dashboard": "dashboard-pro",
  "/analytics": "analytics-dashboard",
  // SaaS
  "/saas": "saas-dashboard",
  "/saas-app": "saas-app",
  // Auth
  "/auth": "auth-flow",
  "/auth-v2": "auth-suite",
  // Social
  "/social": "social-app",
  "/social-v2": "social-v2",
  // Marketing & Landing
  "/landing": "landing-page",
  "/startup": "startup-landing",
  "/marketing": "marketing-site",
  "/marketing-v2": "marketing-v2",
  // Domain-specific
  "/restaurant": "restaurant-menu",
  "/crm": "crm-dashboard",
  "/education": "education-lms",
  "/learn": "edu-platform",
  "/feedback": "feedback-system",
  "/media": "media-hub",
  "/realestate": "real-estate",
  // ActionFlow showcase (testing & verification)
  "/actionflow": "actionflow-showcase",
  // New app templates (2026-03)
  "/food": "food-delivery",
  "/fitness": "fitness-app",
  "/marketplace": "social-marketplace",
  "/tasks": "task-manager",
  // Feature showcase — transform/transition/effects (2026-03)
  "/agency": "agency-studio",
  "/product": "product-launch",
  "/devfolio": "dev-portfolio",
  "/ai": "ai-saas-landing",
  // Quality benchmark — 21st.dev Web API vs Cogo MCP (2026-03)
  "/quality-21st": "quality-21st",
  "/quality-cogo": "quality-cogo",
  // CLI pipeline quality test (2026-03)
  "/cli-coffee": "cli-coffee",
  // Full app generation test (2026-03)
  "/bean-and-brew": "bean-and-brew",
  // Gemini direct JSON pipeline test (2026-03)
  "/bean-v2": "bean-and-brew-v2",
  // Ontology-enriched block generation test (2026-03)
  "/ontology-test": "ontology-test",
  // E2E generated apps — Gemini Direct JSON pipeline (2026-03)
  "/fitlog": "fitlog-e2e",
  "/recipebox": "recipebox-e2e",
  "/budgetbuddy": "budgetbuddy-e2e",
  "/taskflow": "taskflow-e2e",
  // Prompt Template Engine Quality Test (2026-03)
  "/test-dashboard": "test-dashboard",
  "/test-fitness": "test-fitness",
  "/test-portfolio": "test-portfolio",
  "/test-booking": "test-booking",
  "/test-event": "test-event",
  // 6-Tier Design Quality Benchmark (2026-03)
  "/bench/21st": "quality-21st",
  "/bench/converted": "reference-21st",
  "/bench/gemini-pro": "generated-gemini-pro",
  "/bench/gemini-flash": "generated-gemini-flash",
  "/bench/claude": "generated-claude",
  "/bench/21st-api": "generated-21st-api",
  // Homepage is now a React page — see pages/homepage/HomePage.tsx
  // Admin PoC — ActionFlow-based backoffice verification (2026-03)
  "/admin-poc": "admin-poc",
  // A/B Pipeline Comparison — Bridge vs Direct (2026-03)
  "/ab-bridge": "ab-bridge",
  "/ab-direct": "ab-direct",
  // Featured homepage templates (2026-03)
  "/shop": "influencer-fashion",
  "/catalog": "korea-proxy-catalog",
  "/hub": "creator-hub",
  "/ecommerce": "ecommerce-demo",
  "/restaurant-demo": "restaurant-demo",
  "/dashboard-demo": "dashboard-demo",
  "/portfolio-demo": "portfolio-demo",
}

/** Strip Vite base path (e.g. "/preview/") from pathname for routing. */
const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, "") // "/preview" or ""
function stripBase(pathname: string): string {
  if (BASE_PATH && pathname.startsWith(BASE_PATH)) {
    return pathname.slice(BASE_PATH.length) || "/"
  }
  return pathname
}
function withBase(route: string): string {
  return BASE_PATH + route
}

function resolveTemplate(pathname: string): string | null {
  for (const [prefix, template] of Object.entries(TEMPLATE_MAP)) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return template
    }
  }
  return null
}

/** Find the TEMPLATE_MAP prefix for a given pathname. */
function resolvePrefix(pathname: string): string | null {
  for (const prefix of Object.keys(TEMPLATE_MAP)) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return prefix
    }
  }
  return null
}

/** Strip the template prefix from the browser path to get the app-internal route.
 *  e.g. "/food" → "/", "/food/cart" → "/cart" */
function stripPrefix(pathname: string, prefix: string): string {
  if (pathname === prefix) return "/"
  return pathname.slice(prefix.length) || "/"
}

type TemplateCategory = {
  label: string
  items: { route: string; label: string; desc: string; blocks: number }[]
}

const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  {
    label: "E-Commerce",
    items: [
      { route: "/bean-v2", label: "Bean & Brew Coffee Shop", desc: "Menu with category filters, cart, and checkout for cafes & restaurants", blocks: 7 },
    ],
  },
  {
    label: "SaaS & Landing",
    items: [
      { route: "/agency", label: "Agency Studio", desc: "Creative agency landing with portfolio showcase and process overview", blocks: 5 },
      { route: "/ai", label: "AI SaaS Platform", desc: "AI infrastructure landing with features, pricing, and API documentation", blocks: 6 },
      { route: "/product", label: "Product Launch", desc: "Product launch page with hero, features, stats, and call-to-action", blocks: 4 },
    ],
  },
  {
    label: "Portfolios",
    items: [
      { route: "/devfolio", label: "Dev Portfolio", desc: "Developer portfolio with terminal hero, tech stack, and project gallery", blocks: 5 },
    ],
  },
]

function TemplateLanding({ onNavigate }: { onNavigate: (route: string) => void }) {
  /** Derive thumbnail slug from route, e.g. "/store" → "store" */
  const thumbFor = (route: string) => `/thumbnails${route}.png`

  return (
    <div className="min-h-screen bg-background flex flex-col items-center p-8">
      <div className="max-w-6xl w-full">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold mb-2">cogo Template Gallery</h1>
          <p className="text-muted-foreground">
            5 templates — powered by cogo JSON runtime
          </p>
        </div>

        <div className="space-y-10">
          {TEMPLATE_CATEGORIES.map((cat) => (
            <section key={cat.label}>
              <h2 className="text-lg font-semibold text-muted-foreground mb-4 border-b border-border pb-2">
                {cat.label}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {cat.items.map((t) => (
                  <button
                    key={t.route}
                    onClick={() => onNavigate(t.route)}
                    className="group flex flex-col overflow-hidden rounded-xl border bg-card hover:border-primary hover:shadow-lg transition-all text-left"
                  >
                    <div className="relative aspect-video w-full overflow-hidden bg-muted">
                      <img
                        src={thumbFor(t.route)}
                        alt={t.label}
                        loading="lazy"
                        className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex flex-col gap-1 p-3">
                      <div className="flex items-center justify-between w-full">
                        <span className="font-semibold text-sm">{t.label}</span>
                        <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {t.blocks} blocks
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground leading-snug line-clamp-2">{t.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Tenant mode — renders a single app from __COGO_APP__ schema URL */
function TenantApp({ config }: { config: { slug: string; schemaUrl: string } }) {
  const [currentRoute, setCurrentRoute] = useState(window.location.pathname)
  const [app, setApp] = useState<LoadedApp | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadFromSchemaUrl(config.schemaUrl)
      .then(setApp)
      .catch((err) => setError(err.message))
  }, [config.schemaUrl])

  useEffect(() => {
    const onPopState = () => setCurrentRoute(window.location.pathname)
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [])

  const handleNavigate = useCallback((route: string) => {
    setCurrentRoute(route)
    window.history.pushState(null, "", route)
  }, [])

  const handleToast = useCallback((params: ToastParams) => {
    if (params.variant === "success") {
      toast.success(params.message, { description: params.description })
    } else if (params.variant === "error" || params.variant === "destructive") {
      toast.error(params.message, { description: params.description })
    } else {
      toast(params.message, { description: params.description })
    }
  }, [])

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-destructive">
        Failed to load app: {error}
      </div>
    )
  }
  if (!app) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Loading...
      </div>
    )
  }

  return (
    <CogoApp
      manifest={app.manifest}
      blocks={app.blocks}
      data={app.data}
      flows={app.flows}
      designTokens={app.tokens}
      currentRoute={currentRoute}
      onNavigate={handleNavigate}
      onToast={handleToast}
    />
  )
}

export function App() {
  // Tenant mode: __COGO_APP__ injected by Cloudflare tenant router
  const tenantConfig = window.__COGO_APP__
  if (tenantConfig) {
    return <TenantApp config={tenantConfig} />
  }

  // Auth state for onboarding redirect
  const { needsOnboarding, isLoading: authLoading } = useAuth()

  // Dev/gallery mode: local template selection
  const [currentRoute, setCurrentRoute] = useState(stripBase(window.location.pathname))
  const [app, setApp] = useState<LoadedApp | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null)

  const isVerifyPage = currentRoute === "/dev/verify"
  const isRnPreview = currentRoute.startsWith("/rn-preview")
  const isComparePage = currentRoute === "/compare"
  const isLoginPage = currentRoute === "/login"
  const isAuthCallback = currentRoute === "/login/callback"
  const isOnboarding = currentRoute === "/onboarding"
  const isHomepage = currentRoute === "/homepage"
  const isGallery = currentRoute === "/gallery" || currentRoute === "/"
  const templateId = useMemo(
    () => (isVerifyPage || isRnPreview || isComparePage || isLoginPage || isAuthCallback || isOnboarding || isHomepage || isGallery ? null : resolveTemplate(currentRoute)),
    [currentRoute, isVerifyPage, isRnPreview, isComparePage, isLoginPage, isAuthCallback, isOnboarding, isHomepage, isGallery],
  )

  // Load template when route prefix changes
  useEffect(() => {
    if (isVerifyPage || isRnPreview || isLoginPage || isAuthCallback || isOnboarding || isHomepage || isGallery) return
    if (!templateId) {
      setApp(null)
      setActiveTemplate(null)
      return
    }
    if (templateId === activeTemplate) return

    setApp(null)
    setError(null)
    loadApp(`/apps/${templateId}`)
      .then((loaded) => {
        setApp(loaded)
        setActiveTemplate(templateId)
      })
      .catch((err) => setError(err.message))
  }, [templateId, activeTemplate, isVerifyPage])

  // Sync browser back/forward with internal route state
  useEffect(() => {
    const onPopState = () => {
      setCurrentRoute(stripBase(window.location.pathname))
    }
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [])

  const routePrefix = useMemo(() => resolvePrefix(currentRoute), [currentRoute])

  const handleNavigate = useCallback((route: string) => {
    // If the route already starts with a known prefix, use it directly
    if (resolveTemplate(route)) {
      setCurrentRoute(route)
      window.history.pushState(null, "", withBase(route))
    } else if (routePrefix) {
      // Add prefix back for browser URL (CogoApp sends app-internal routes like "/cart")
      const fullRoute = routePrefix + (route === "/" ? "" : route)
      setCurrentRoute(fullRoute)
      window.history.pushState(null, "", withBase(fullRoute))
    } else {
      setCurrentRoute(route)
      window.history.pushState(null, "", withBase(route))
    }
  }, [routePrefix])

  const handleToast = useCallback((params: ToastParams) => {
    if (params.variant === "success") {
      toast.success(params.message, { description: params.description })
    } else if (params.variant === "error" || params.variant === "destructive") {
      toast.error(params.message, { description: params.description })
    } else {
      toast(params.message, { description: params.description })
    }
  }, [])

  // Side-by-side template comparison
  if (isComparePage) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading Compare...</div>}>
        <ComparePage />
      </Suspense>
    )
  }

  // Auth pages
  if (isAuthCallback) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#030014] text-white/40">Loading...</div>}>
        <AuthCallback />
      </Suspense>
    )
  }
  if (isLoginPage) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#030014] text-white/40">Loading...</div>}>
        <AuthPage />
      </Suspense>
    )
  }
  if (isOnboarding) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#030014] text-white/40">Loading...</div>}>
        <OnboardingPage />
      </Suspense>
    )
  }

  // Redirect to onboarding if authenticated but not onboarded
  // (skip for auth routes and onboarding page itself)
  if (!authLoading && needsOnboarding && !isLoginPage && !isAuthCallback && !isOnboarding) {
    window.history.replaceState(null, "", withBase("/onboarding"))
    if (currentRoute !== "/onboarding") {
      setCurrentRoute("/onboarding")
    }
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#030014] text-white/40">Loading...</div>}>
        <OnboardingPage />
      </Suspense>
    )
  }

  // React Native Web preview — renders via @cogo/react-native-renderer + react-native-web
  if (isRnPreview) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading RN Preview...</div>}>
        <RnPreviewPage />
      </Suspense>
    )
  }

  // Dev-only verification page
  if (isVerifyPage) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
        <VerifyPage />
      </Suspense>
    )
  }

  // Homepage with hero + featured templates
  if (isHomepage) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-muted-foreground">Loading...</div>}>
        <HomePage onNavigate={handleNavigate} />
      </Suspense>
    )
  }

  // Template gallery
  if (isGallery) {
    return <TemplateLanding onNavigate={handleNavigate} />
  }

  // No template matched → show gallery
  if (!templateId) {
    return <TemplateLanding onNavigate={handleNavigate} />
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-destructive">
        Failed to load app: {error}
      </div>
    )
  }

  if (!app) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Loading...
      </div>
    )
  }

  return (
    <CogoApp
      manifest={app.manifest}
      blocks={app.blocks}
      data={app.data}
      flows={app.flows}
      designTokens={app.tokens}
      basePath={`/apps/${activeTemplate}`}
      currentRoute={routePrefix ? stripPrefix(currentRoute, routePrefix) : currentRoute}
      onNavigate={handleNavigate}
      onToast={handleToast}
    />
  )
}
